<template>
  <div class="bg-zinc-900 text-white py-4 sm:py-8 sm:p-8">
    <!-- Header Section -->
    <div class="max-w-7xl mx-auto mb-12">
      <h1 class="text-4xl font-bold mb-4">Ajuda</h1>
      <p class="text-lg text-zinc-300 max-w-3xl">
        Descubra tutoriais detalhados e guiados que ajudam você a explorar todos os recursos da nossa plataforma com praticidade e eficiência!
      </p>
    </div>

    <!-- Enhanced YouTube Tutorial Banner with Social Links -->
    <div class="max-w-7xl mx-auto mb-10">
      <div class="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 shadow-2xl">
        <div class="flex flex-col gap-6">
          <!-- Main Tutorial Section -->
          <div class="flex flex-col md:flex-row items-center justify-between gap-4">
            <div class="flex items-center gap-4">
              <div class="bg-white bg-opacity-10 rounded-full p-3">
                <Play class="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 class="text-xl font-bold text-white mb-1">Tutorial Completo do Bot</h3>
                <p class="text-emerald-100 text-sm">Assista ao tutorial completo no YouTube e domine todas as funcionalidades</p>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <a 
                href="https://www.youtube.com/watch?v=lNMLcKutWxs" 
                target="_blank"
                class="bg-white text-emerald-700 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5 cursor-pointer shadow-lg"
              >
                <ExternalLink class="h-4 w-4" />
                Assistir Tutorial
              </a>
            </div>
          </div>

          <!-- Social Media Links Section -->
          <div class="border-t border-emerald-500/30 pt-4">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div class="flex items-center gap-3">
                <Users class="h-6 w-6 text-emerald-100" />
                <div>
                  <h4 class="text-white font-semibold">Junte-se à Nossa Comunidade Parceira</h4>
                  <p class="text-emerald-100 text-sm">Conecte-se com outros usuários e receba suporte</p>
                </div>
              </div>
              
              <div class="flex items-center gap-3">
                <img 
                  src="/img/arion-company.jfif" 
                  alt="Arion Company" 
                  class="h-[60px] w-[100px] rounded-md shadow-lg" 
                />
                <!-- WhatsApp Group Link -->
                <a 
                  href="https://chat.whatsapp.com/EiajkhhMbYJIfgA9FXm5uC?mode=ems_copy_c"
                  target="_blank"
                  class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 transform shadow-lg"
                >
                  <WhatsappIcon class="h-4 w-4 fill-white" />
                  WhatsApp
                </a>
                
                <!-- Telegram Group Link -->
                <a 
                  href="https://t.me/+L-wgr7EBS_pkMmIx"
                  target="_blank"
                  class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 transform shadow-lg"
                >
                  <TelegramIcon class="h-4 w-4 fill-white stroke-white" />
                  Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tutorial Content or Video Grid -->
    <div class="max-w-7xl mx-auto">
      <div v-if="activeTutorial">
        <div class="mb-6 flex items-center">
          <button 
            @click="closeTutorial" 
            class="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft class="h-5 w-5" />
            Voltar para todos os tutoriais
          </button>
        </div>
        
        <!-- Tutorial Components would be rendered here -->
        <div class="bg-zinc-800 rounded-xl p-6">
          <h2 class="text-2xl font-bold mb-4">{{ activeTutorial.title }}</h2>
          <p class="text-zinc-300">{{ activeTutorial.description }}</p>
        </div>
      </div>
      
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div 
          v-for="(video, index) in filteredVideos" 
          :key="index"
          class="bg-zinc-800 rounded-lg overflow-hidden transition-transform duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer"
          @click="openTutorial(video)"
        >
          <div class="relative">
            <img :src="video.thumbnail" :alt="video.title" class="w-full object-cover h-[15rem]" />
          </div>
          <div class="p-4">
            <h3 class="text-white font-medium mb-1">{{ video.title }}</h3>
            <p class="text-zinc-400 text-sm">Suporte Bot Express</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Modal -->
    <Teleport to="body">
      <div v-if="selectedVideo" class="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div class="bg-zinc-900 rounded-xl overflow-hidden max-w-4xl w-full">
          <div class="relative pt-[56.25%] w-full bg-black">
            <iframe 
              v-if="selectedVideo"
              class="absolute inset-0 w-full h-full"
              :src="`https://www.youtube.com/embed/${getYoutubeId(selectedVideo.videoUrl)}?autoplay=1`"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen
            ></iframe>
          </div>
          <div class="p-6">
            <div class="flex justify-between items-start">
              <div>
                <h3 class="text-xl font-bold mb-2">{{ selectedVideo.title }}</h3>
                <p class="text-zinc-400">Suporte Bot</p>
              </div>
              <button 
                @click="closeVideoModal" 
                class="bg-zinc-800 hover:bg-zinc-700 transition-colors duration-300 rounded-full p-2"
              >
                <X class="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { 
  Search, 
  Play, 
  X, 
  ArrowLeft, 
  SendHorizontal, 
  ExternalLink, 
  MessageCircle, 
  Send, 
  Users 
} from 'lucide-vue-next'
import WhatsappIcon from '@/components/ui/icons/WhatsappIcon.vue'
import TelegramIcon from '@/components/ui/icons/TelegramIcon.vue'

// Video data
const videos = ref([
  {
    id: 1,
    title: 'Crie Seu Primeiro Bot do Telegram',
    description: 'Aprenda a criar seu bot no Telegram e vincular no painel para desbloquear todas as funcionalidades.',
    thumbnail: '/assets/help/card-help-make-bot.png',
  },
  {
    id: 2,
    title: 'Adicione seu Canal ou Grupo do Telegram',
    description: 'Como adicionar seu canal ou grupo facilmente.',
    thumbnail: '/assets/help/card-help-channels-groups.png',
  },
  {
    id: 3,
    title: 'Crie Seu Plano de Pagamento no Bot Express',
    description: 'Como configurar planos de pagamento para seu bot na Bot Express.',
    thumbnail: '/assets/help/card-help-plans.png',
  },
  {
    id: 4,
    title: 'Configure disparos de mensagens no Bot Express',
    description: 'Como configurar planos de pagamento para seu bot na Bot Express.',
    thumbnail: '/assets/help/card-help-dispatches.png',
  },
  {
    id: 5,
    title: 'Configure os Botões do Seu Bot no Bot Express',
    description: 'Como personalizar e configurar os botões do seu bot na plataforma.',
    thumbnail: '/assets/help/card-help-buttons.png',
  },
])

// State
const searchQuery = ref('')
const selectedVideo = ref(null)
const activeTutorial = ref(null)

// Computed
const filteredVideos = computed(() => {
  if (!searchQuery.value) return videos.value
  const query = searchQuery.value.toLowerCase()
  return videos.value.filter(video => 
    video.title.toLowerCase().includes(query) || 
    video.description.toLowerCase().includes(query)
  )
})

const closeVideoModal = () => {
  selectedVideo.value = null
  document.body.style.overflow = 'auto'
}

const getYoutubeId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return (match && match[2].length === 11) ? match[2] : null
}

const openTutorial = (video) => {
  activeTutorial.value = video
}

const closeTutorial = () => {
  activeTutorial.value = null
}

const showNextTutorial = () => {
  const currentIndex = videos.value.findIndex(v => v.id === activeTutorial.value.id)
  if (currentIndex < videos.value.length - 1) {
    const nextVideo = videos.value[currentIndex + 1]
    activeTutorial.value = nextVideo
  }
}
</script>